import { CloseCircleFilled, FileAddOutlined, LinkOutlined, PictureOutlined } from '@ant-design/icons';
import { AttachedImage, ChannelMessage, ChannelMessageDoc, ChannelMode } from '@gooduncles/gu-app-schema';
import { Image as AntImage, Button, Checkbox, Input, Select, Spin, message } from 'antd';
import { ChangeEvent, FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { IMAGE_BUCKET } from 'src/constants/storage.constants';
import { useAuth } from 'src/stores/auth-context';

import { markChannelInboxRead } from 'src/utils/channel/channel-inbox-util';
import {
  ChannelMessageWithCollection,
  fetchAnnotatedChannelMessages,
  observeAnnotatedChannelMessages,
} from 'src/utils/channel/channel-message-util';
import { formatChatMessageTime, formatDate } from 'src/utils/date-util';
import { QueryOptions, WHERE } from 'src/utils/firebase/firebase-manager';
import {
  createNoTriggerChannelMessage,
  createTriggerChannelMessage,
  getChannel,
  getImageFileOriginalId,
} from 'src/utils/firebase/firebase-short-cut';
import { generateThumbnail, uploadImageToStorage } from 'src/utils/image-util';
import {
  STORE_CHANNEL_PREFIX,
  SelectedMessageRef,
  toSelectedMessageRef,
} from 'src/utils/issue/issue-case-message-link-util';

import CreateCaseFromMessagesModal from 'src/components/Issue/MessageLink/CreateCaseFromMessagesModal';

import styles from './ChatRoomDetail.module.scss';

const PAGE_SIZE = 30;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const VALID_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

const getExtensionFromMimeType = (mimeType: string): string => {
  if (mimeType === 'image/png') return 'png';
  if (mimeType === 'image/webp') return 'webp';
  return 'jpg';
};

interface PendingImage {
  id: string;
  file: File;
  previewUrl: string;
}

/**
 * 채널 이미지의 Storage 경로 prefix를 반환한다.
 * - 매장 채널(store_<storeId>, conversation): 고객앱(gu-commerce-web)과 동일하게 `store/<storeId>`로 통일.
 * - 그 외 채널(notice/promotion, broadcast): 단방향 공지·프로모션 이미지이므로 `broadcast/<channelId>`로 분리.
 */
const getChannelImagePathPrefix = (channelId: string): string =>
  channelId.startsWith(STORE_CHANNEL_PREFIX)
    ? `store/${channelId.slice(STORE_CHANNEL_PREFIX.length)}`
    : `broadcast/${channelId}`;

/** 이미지 원본/썸네일을 Storage에 업로드하고 AttachedImage 메타를 반환 */
const uploadChannelMessageImage = async (
  channelId: string,
  file: File,
  index: number,
): Promise<{ imageId: string; image: AttachedImage }> => {
  const imageFileId = getImageFileOriginalId();
  const thumbnailFile = await generateThumbnail(file);

  const originalExt = getExtensionFromMimeType(file.type);
  const thumbnailExt = getExtensionFromMimeType(thumbnailFile.type);
  const pathPrefix = getChannelImagePathPrefix(channelId);
  const originalPath = `${pathPrefix}/original/${imageFileId}.${originalExt}`;
  const thumbnailPath = `${pathPrefix}/thumbnail/${imageFileId}.${thumbnailExt}`;

  const [originalUrl, thumbnailUrl] = await Promise.all([
    uploadImageToStorage(IMAGE_BUCKET, file, originalPath),
    uploadImageToStorage(IMAGE_BUCKET, thumbnailFile, thumbnailPath),
  ]);

  return { imageId: imageFileId, image: { index, originalUrl, thumbnailUrl } };
};

/** 두 컬렉션을 병렬 조회하여 병합 (커서 기반 페이지네이션) */
const fetchChannelMessages = async (
  channelId: string,
  cursor?: string,
  pageSize = PAGE_SIZE,
): Promise<{ messages: ChannelMessageWithCollection[]; nextCursor: string | undefined }> => {
  const wheres: WHERE[] = [['channelId', '==', channelId], ...(cursor ? [['createdAt', '<', cursor] as WHERE] : [])];
  const options: QueryOptions = { sortKey: 'createdAt', orderBy: 'desc', limit: pageSize };

  const annotated = await fetchAnnotatedChannelMessages(wheres, options);
  const unique = new Map<string, ChannelMessageWithCollection>();
  for (const doc of annotated) {
    if (!unique.has(doc._id)) unique.set(doc._id, doc);
  }
  const sorted = Array.from(unique.values()).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  const sliced = sorted.slice(0, pageSize);
  const nextCursor = sliced.length === pageSize ? sliced[sliced.length - 1].createdAt : undefined;
  return { messages: sliced, nextCursor };
};

/** 메시지 Map에 병합하고 ASC 정렬된 배열로 반환 */
const mergeMessages = (
  existing: Map<string, ChannelMessageWithCollection>,
  incoming: ChannelMessageWithCollection[],
): { map: Map<string, ChannelMessageWithCollection>; list: ChannelMessageWithCollection[] } => {
  const merged = new Map(existing);
  for (const m of incoming) merged.set(m._id, m);
  return {
    map: merged,
    list: Array.from(merged.values()).sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
  };
};

interface ChatRoomDetailProps {
  channelId?: string;
  onMessageSent?: () => void;
  headerExtra?: ReactNode;
}

const ChatRoomDetail: FC<ChatRoomDetailProps> = ({ channelId: channelIdProp, onMessageSent, headerExtra }) => {
  const { channelId: channelIdParam } = useParams<{ channelId: string }>();
  const channelId = channelIdProp ?? channelIdParam;
  const { user } = useAuth();

  const [messages, setMessages] = useState<ChannelMessageWithCollection[]>([]);
  const messageMapRef = useRef(new Map<string, ChannelMessageWithCollection>());
  // 실시간 구독 effect 가 매 메시지마다 재구독되지 않도록 ref 로 latest 값을 읽는다.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const [channelName, setChannelName] = useState('');
  const [channelMode, setChannelMode] = useState<ChannelMode>('conversation');
  const [inputText, setInputText] = useState('');
  const [sendWithPush, setSendWithPush] = useState(true);
  const [sending, setSending] = useState(false);
  const [showAction, setShowAction] = useState(false);
  const [actionType, setActionType] = useState<string | undefined>(undefined);
  const [actionValue, setActionValue] = useState('');

  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isScrollReady, setIsScrollReady] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const cursorRef = useRef<string | undefined>(undefined);

  // 케이스 등록을 위한 메시지 다중 선택 모드
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [createCaseModalOpen, setCreateCaseModalOpen] = useState(false);

  // 채널이 바뀌면 선택 상태 초기화 (다른 매장 메시지를 같이 묶지 못하게)
  useEffect(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  }, [channelId]);

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const exitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  }, []);

  const messageById = useMemo(() => {
    const map = new Map<string, ChannelMessageWithCollection>();
    messages.forEach((m) => map.set(m._id, m));
    return map;
  }, [messages]);

  const selectedMessageRefs: SelectedMessageRef[] = useMemo(
    () =>
      Array.from(selectedIds)
        .map((id) => messageById.get(id))
        .filter((doc): doc is ChannelMessageWithCollection => !!doc)
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
        .map(toSelectedMessageRef),
    [selectedIds, messageById],
  );

  const selectedMessageDocsById = useMemo(() => {
    const acc: Record<string, ChannelMessageDoc | undefined> = {};
    selectedMessageRefs.forEach((ref) => {
      acc[ref.messageId] = messageById.get(ref.messageId);
    });
    return acc;
  }, [selectedMessageRefs, messageById]);

  const storeIdFromChannel = useMemo(() => {
    if (!channelId?.startsWith(STORE_CHANNEL_PREFIX)) return null;
    return channelId.slice(STORE_CHANNEL_PREFIX.length);
  }, [channelId]);

  const handleOpenCreateCase = () => {
    if (selectedIds.size === 0) {
      message.warning('메시지를 1건 이상 선택해주세요.');
      return;
    }
    if (!storeIdFromChannel) {
      message.error('매장 채널이 아니라 케이스를 등록할 수 없습니다.');
      return;
    }
    setCreateCaseModalOpen(true);
  };

  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    return () => {
      pendingImages.forEach((img) => URL.revokeObjectURL(img.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilesSelected = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length === 0) return;

    const accepted: PendingImage[] = [];
    for (const file of files) {
      if (!VALID_IMAGE_TYPES.includes(file.type)) {
        message.error(`지원하지 않는 이미지 형식입니다: ${file.name}`);
        continue;
      }
      if (file.size > MAX_IMAGE_SIZE) {
        message.error(`이미지 크기는 5MB 이하여야 합니다: ${file.name}`);
        continue;
      }
      accepted.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        previewUrl: URL.createObjectURL(file),
      });
    }
    if (accepted.length > 0) setPendingImages((prev) => [...prev, ...accepted]);
  };

  const removePendingImage = (id: string) => {
    setPendingImages((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  };

  // 채널명 조회
  useEffect(() => {
    if (!channelId) return;
    getChannel(channelId).then((ch) => {
      if (ch) {
        setChannelName(ch.name);
        setChannelMode(ch.mode);
      }
    });
  }, [channelId]);

  // 초기 메시지 로드
  useEffect(() => {
    if (!channelId) return;

    isInitialLoadRef.current = true;
    messageMapRef.current = new Map();
    setMessages([]);
    setIsLoading(true);
    setIsScrollReady(false);
    setHasNextPage(false);
    cursorRef.current = undefined;

    let cancelled = false;
    let attachedScrollEl: HTMLDivElement | null = null;
    const releaseInitial = () => {
      isInitialLoadRef.current = false;
    };

    fetchChannelMessages(channelId).then(({ messages: fetched, nextCursor }) => {
      if (cancelled) return;
      const { map: newMap, list } = mergeMessages(new Map(), fetched);
      messageMapRef.current = newMap;
      setMessages(list);
      cursorRef.current = nextCursor;
      setHasNextPage(!!nextCursor);
      setIsLoading(false);

      // 초기 로드 시 스크롤을 맨 아래로 — 이미지 로드 등으로 콘텐츠 높이가 늘어나도 따라가도록
      // 일정 시간 동안 매 프레임 보정. 사용자가 스크롤 의도를 보이면(wheel/touchmove) 즉시 해제.
      const el = scrollRef.current;
      if (!el) {
        setIsScrollReady(true);
        return;
      }
      attachedScrollEl = el;
      el.addEventListener('wheel', releaseInitial, { passive: true });
      el.addEventListener('touchmove', releaseInitial, { passive: true });

      const start = performance.now();
      let revealed = false;
      const tick = () => {
        if (cancelled || !isInitialLoadRef.current) return;
        const node = scrollRef.current;
        if (node) node.scrollTop = node.scrollHeight;
        // 첫 스크롤이 적용된 직후 노출 — 같은 프레임 내 commit으로 깜빡임 방지
        if (!revealed) {
          revealed = true;
          setIsScrollReady(true);
        }
        if (performance.now() - start > 1500) {
          isInitialLoadRef.current = false;
          return;
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });

    return () => {
      cancelled = true;
      isInitialLoadRef.current = false;
      if (attachedScrollEl) {
        attachedScrollEl.removeEventListener('wheel', releaseInitial);
        attachedScrollEl.removeEventListener('touchmove', releaseInitial);
      }
    };
  }, [channelId]);

  // 실시간 신규 메시지 구독 (초기 로드 완료 후)
  useEffect(() => {
    if (!channelId || isLoading) return;

    const latest = messagesRef.current;
    const latestCreatedAt =
      latest.length > 0 ? latest[latest.length - 1].createdAt : formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900");

    const wheres: WHERE[] = [
      ['channelId', '==', channelId],
      ['createdAt', '>', latestCreatedAt],
    ];
    const options: QueryOptions = { sortKey: 'createdAt', orderBy: 'desc' };

    const subscription = observeAnnotatedChannelMessages(wheres, options).subscribe({
      next: (incoming) => {
        if (incoming.length === 0) return;
        const { map: newMap, list } = mergeMessages(messageMapRef.current, incoming);
        messageMapRef.current = newMap;
        setMessages(list);

        // 보는 중 새 고객 메시지가 도착하면 받은편지함 안읽음 플래그를 다시 해제한다.
        if (incoming.some((m) => m.senderService === 'commerce')) {
          markChannelInboxRead(channelId);
        }

        // 새 메시지 수신 시 하단 근처에 있으면 자동 스크롤
        requestAnimationFrame(() => {
          const el = scrollRef.current;
          if (!el) return;
          const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
          if (isNearBottom) {
            el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
          }
        });
      },
    });

    return () => subscription.unsubscribe();
  }, [channelId, isLoading]);

  // 과거 메시지 로드
  const loadMore = useCallback(async () => {
    if (!channelId || isFetchingMore || !hasNextPage) return;
    setIsFetchingMore(true);

    const el = scrollRef.current;
    const prevScrollHeight = el?.scrollHeight ?? 0;
    const prevScrollTop = el?.scrollTop ?? 0;

    const { messages: fetched, nextCursor } = await fetchChannelMessages(channelId, cursorRef.current);
    const { map: newMap, list } = mergeMessages(messageMapRef.current, fetched);
    messageMapRef.current = newMap;
    setMessages(list);
    cursorRef.current = nextCursor;
    setHasNextPage(!!nextCursor);
    setIsFetchingMore(false);

    // 스크롤 위치 유지: prepend로 늘어난 높이만큼 더해 사용자가 보던 메시지를 그대로 유지
    requestAnimationFrame(() => {
      if (el) {
        const delta = el.scrollHeight - prevScrollHeight;
        el.scrollTop = prevScrollTop + delta;
      }
    });
  }, [channelId, isFetchingMore, hasNextPage]);

  // IntersectionObserver: 위로 스크롤 시 과거 메시지 로드
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = scrollRef.current;
    if (!sentinel || !container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingMore && !isLoading) {
          loadMore();
        }
      },
      { root: container, rootMargin: '200px 0px 0px 0px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingMore, isLoading, loadMore]);

  // 메시지 전송
  const handleSend = async () => {
    const trimmedText = inputText.trim();
    if ((!trimmedText && pendingImages.length === 0) || !channelId || !user || sending) return;

    const imagesToUpload = pendingImages;
    const now = formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900");

    setSending(true);
    setInputText('');
    setPendingImages([]);

    try {
      let imagesMap: Record<string, AttachedImage> | null = null;
      if (imagesToUpload.length > 0) {
        const uploaded = await Promise.all(
          imagesToUpload.map((img, index) => uploadChannelMessageImage(channelId, img.file, index)),
        );
        imagesMap = uploaded.reduce<Record<string, AttachedImage>>((acc, { imageId, image }) => {
          acc[imageId] = image;
          return acc;
        }, {});
      }

      const messageData: ChannelMessage = {
        channelId,
        channelMode,
        senderService: 'console',
        senderCollection: 'user',
        senderId: user._id,
        title: user.userName ?? 'console',
        body: trimmedText || null,
        images: imagesMap,
        type: 'chat',
        actionType: actionType ?? null,
        actionValue: actionType && actionValue ? actionValue : null,
        category: 'customer-service',
        createdAt: now,
        scheduledAt: null,
        scheduleStatus: null,
        claimedAt: null,
      };

      if (sendWithPush) {
        await createTriggerChannelMessage(messageData);
      } else {
        await createNoTriggerChannelMessage(messageData);
      }
      imagesToUpload.forEach((img) => URL.revokeObjectURL(img.previewUrl));
      onMessageSent?.();
      setShowAction(false);
      setActionType(undefined);
      setActionValue('');
    } catch (error) {
      console.error('Failed to send message:', error);
      message.error('메시지 전송에 실패했습니다.');
      setInputText(trimmedText);
      setPendingImages(imagesToUpload);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  const isEmpty = !isLoading && messages.length === 0;
  // 초기 로드 직후 bottom-snap이 적용되기 전까지 메시지 영역을 숨겨 깜빡임 방지
  const hideMessageArea = !isLoading && !isScrollReady;

  const messageElements = useMemo(
    () =>
      messages.map((msg) => {
        const isOurs = msg.senderService !== 'commerce';
        const isMe = isOurs && msg.senderId === user?._id;
        const isOtherAdmin = isOurs && !isMe;
        const bodyText = msg.body ?? '';
        const hasImages = !!msg.images && Object.keys(msg.images).length > 0;
        const hasBody = bodyText.length > 0;
        const timeText = formatChatMessageTime(new Date(msg.createdAt));
        const isSelected = selectedIds.has(msg._id);

        const sortedImages = hasImages
          ? Object.entries(msg.images as Record<string, AttachedImage>)
              .map(([imageId, image]) => ({ imageId, ...image }))
              .sort((a, b) => a.index - b.index)
          : [];

        const imageGroup = hasImages ? (
          <AntImage.PreviewGroup>
            <div className={styles.messageImages}>
              {sortedImages.map((image) => (
                <AntImage
                  key={image.imageId}
                  src={image.thumbnailUrl || image.originalUrl}
                  preview={{ src: image.originalUrl }}
                  className={styles.messageImage}
                />
              ))}
            </div>
          </AntImage.PreviewGroup>
        ) : null;

        const fallbackText = !hasBody && !hasImages ? msg.title : '';

        const bubbleContent = (() => {
          if (isMe) {
            return (
              <div className={styles.bubbleRowOwn}>
                {imageGroup}
                {(hasBody || fallbackText) && <div className={styles.bubbleOwn}>{bodyText || fallbackText}</div>}
                <span className={styles.timeOwn}>{timeText}</span>
              </div>
            );
          }
          if (isOtherAdmin) {
            return (
              <div className={styles.bubbleRowAdmin}>
                <span className={styles.adminLabel}>{msg.title || '관리자'}</span>
                {imageGroup}
                {(hasBody || fallbackText) && <div className={styles.bubbleAdmin}>{bodyText || fallbackText}</div>}
                <span className={styles.timeOwn}>{timeText}</span>
              </div>
            );
          }
          return (
            <div className={styles.bubbleRowOther}>
              <span className={styles.senderLabel}>{msg.title}</span>
              {imageGroup}
              {(hasBody || fallbackText) && <div className={styles.bubbleOther}>{bodyText || fallbackText}</div>}
              <span className={styles.timeOther}>{timeText}</span>
            </div>
          );
        })();

        if (!selectionMode) {
          return (
            <div key={msg._id} className={styles.selectableWrapper}>
              {bubbleContent}
            </div>
          );
        }

        return (
          <div
            key={msg._id}
            className={`${styles.selectableWrapper} ${styles.selectableActive} ${
              isSelected ? styles.selectableSelected : ''
            }`}
            onClick={() => toggleSelected(msg._id)}
          >
            <Checkbox
              checked={isSelected}
              className={styles.selectCheckbox}
              onClick={(e) => e.stopPropagation()}
              onChange={() => toggleSelected(msg._id)}
            />
            <div className={styles.selectableBubble}>{bubbleContent}</div>
          </div>
        );
      }),
    [messages, user?._id, selectionMode, selectedIds, toggleSelected],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 style={{ margin: 0 }}>{channelName || channelId}</h3>
        {headerExtra}
      </div>

      <div
        className={styles.messageArea}
        ref={scrollRef}
        style={hideMessageArea ? { visibility: 'hidden' } : undefined}
      >
        {/* 상단 sentinel: 위로 스크롤 시 과거 메시지 로드 */}
        {hasNextPage && <div ref={sentinelRef} className={styles.sentinel} />}
        {isFetchingMore && (
          <div className={styles.loadingMore}>
            <Spin size='small' />
          </div>
        )}

        {isLoading && (
          <div className={styles.loadingCenter}>
            <Spin />
          </div>
        )}

        {isEmpty && <div className={styles.emptyState}>메시지가 없습니다.</div>}

        {messageElements}
      </div>

      <div className={styles.toolRow}>
        <Checkbox
          checked={sendWithPush}
          onChange={(e) => setSendWithPush(e.target.checked)}
          className={styles.pushToggle}
        >
          FCM 푸시
        </Checkbox>
        <Button
          type={showAction ? 'link' : 'text'}
          icon={<LinkOutlined />}
          onClick={() => setShowAction((prev) => !prev)}
          size='small'
          className={styles.actionToggle}
        />
        <Button
          type='text'
          icon={<PictureOutlined />}
          onClick={() => fileInputRef.current?.click()}
          size='small'
          className={styles.actionToggle}
        />
        {storeIdFromChannel &&
          (selectionMode ? (
            <Button size='small' onClick={exitSelectionMode}>
              선택 모드 종료
            </Button>
          ) : (
            <Button size='small' icon={<FileAddOutlined />} onClick={() => setSelectionMode(true)}>
              메시지 선택
            </Button>
          ))}
      </div>

      {showAction && (
        <div className={styles.actionArea}>
          <div className={styles.actionRow}>
            <Select
              value={actionType}
              onChange={(v) => {
                setActionType(v);
                if (!v) setActionValue('');
              }}
              options={[{ label: '페이지 이동 (navigate)', value: 'navigate' }]}
              placeholder='액션 타입'
              allowClear
              style={{ width: 200 }}
              size='small'
            />
            {actionType && (
              <Input
                value={actionValue}
                onChange={(e) => setActionValue(e.target.value)}
                placeholder='/store/detail'
                size='small'
                style={{ flex: 1 }}
                status={actionValue && !/^\/.+/.test(actionValue) ? 'error' : undefined}
              />
            )}
          </div>
        </div>
      )}

      {pendingImages.length > 0 && (
        <div className={styles.pendingImagesArea}>
          {pendingImages.map((img) => (
            <div key={img.id} className={styles.pendingImageItem}>
              <img src={img.previewUrl} alt='' className={styles.pendingImagePreview} />
              <button
                type='button'
                className={styles.pendingImageRemove}
                onClick={() => removePendingImage(img.id)}
                aria-label='이미지 제거'
              >
                <CloseCircleFilled />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className={styles.inputArea}>
        <input
          ref={fileInputRef}
          type='file'
          accept='image/jpeg,image/png,image/webp'
          multiple
          style={{ display: 'none' }}
          onChange={handleFilesSelected}
        />
        <Input.TextArea
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder='메시지를 입력하세요...'
          autoSize={{ minRows: 1, maxRows: 3 }}
          style={{ flex: 1 }}
        />
        <Button
          type='primary'
          onClick={handleSend}
          disabled={(!inputText.trim() && pendingImages.length === 0) || sending}
          loading={sending}
        >
          전송
        </Button>
      </div>

      {selectionMode && (
        <div className={styles.selectionActionBar}>
          <span className={styles.selectionCount}>{selectedIds.size}건 선택됨</span>
          <Button size='small' onClick={() => setSelectedIds(new Set())} disabled={selectedIds.size === 0}>
            선택 해제
          </Button>
          <Button type='primary' size='small' onClick={handleOpenCreateCase} disabled={selectedIds.size === 0}>
            케이스로 등록
          </Button>
        </div>
      )}

      {storeIdFromChannel && (
        <CreateCaseFromMessagesModal
          open={createCaseModalOpen}
          onClose={() => setCreateCaseModalOpen(false)}
          storeId={storeIdFromChannel}
          selectedMessages={selectedMessageRefs}
          messageDocsById={selectedMessageDocsById}
          onSuccess={() => {
            exitSelectionMode();
          }}
        />
      )}
    </div>
  );
};

export default ChatRoomDetail;
